import React from 'react';
import { motion } from 'framer-motion';
function LeafMotif({
  className,
  delay = 0
}: {
  className?: string;
  delay?: number;
}) {
  return <motion.svg initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 1,
    delay
  }} className={className} viewBox="0 0 60 100" fill="none" aria-hidden="true" data-id="element-72">
      <motion.path animate={{
      rotate: [0, 3, -3, 0],
      y: [0, -5, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    }} d="M30 5C30 5 10 25 10 50C10 75 30 95 30 95C30 95 50 75 50 50C50 25 30 5 30 5Z" fill="currentColor" fillOpacity="0.15" data-id="element-73" />
      <motion.path animate={{
      rotate: [0, 3, -3, 0],
      y: [0, -5, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    }} d="M30 15C30 15 30 85 30 85" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" data-id="element-74" />
    </motion.svg>;
}
function FloatingLeaf({
  className,
  delay = 0
}: {
  className?: string;
  delay?: number;
}) {
  return <motion.svg initial={{
    opacity: 0,
    scale: 0.8
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 1.2,
    delay
  }} className={className} viewBox="0 0 40 60" fill="none" aria-hidden="true" data-id="element-75">
      <motion.g animate={{
      rotate: [-5, 5, -5],
      y: [0, -8, 0],
      x: [0, 3, 0]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut',
      delay
    }} data-id="element-76">
        <path d="M20 5C20 5 5 20 5 35C5 50 20 55 20 55C20 55 35 50 35 35C35 20 20 5 20 5Z" fill="currentColor" fillOpacity="0.12" data-id="element-77" />
        <path d="M20 10L20 50" stroke="currentColor" strokeOpacity="0.25" strokeWidth="0.75" data-id="element-78" />
        <path d="M20 20C14 25 10 30 10 35" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.5" data-id="element-79" />
        <path d="M20 25C26 30 30 33 30 37" stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.5" data-id="element-80" />
      </motion.g>
    </motion.svg>;
}
export function Hero() {
  return <section className="relative min-h-screen bg-cream overflow-hidden" data-id="element-81">
      {/* Background botanical elements */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-82">
        <LeafMotif className="absolute top-20 left-[5%] w-16 h-28 text-sage opacity-60" delay={0.2} data-id="element-83" />
        <LeafMotif className="absolute top-32 right-[8%] w-12 h-20 text-eucalyptus opacity-50" delay={0.5} data-id="element-84" />
        <FloatingLeaf className="absolute top-[40%] left-[3%] w-10 h-16 text-sage opacity-40" delay={0.8} data-id="element-85" />
        <FloatingLeaf className="absolute top-[30%] right-[5%] w-14 h-20 text-eucalyptus opacity-35" delay={1.1} data-id="element-86" />
        <LeafMotif className="absolute bottom-32 left-[10%] w-14 h-24 text-olive opacity-30" delay={0.4} data-id="element-87" />
        <FloatingLeaf className="absolute bottom-40 right-[12%] w-12 h-18 text-sage opacity-45" delay={0.7} data-id="element-88" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6 py-20" data-id="element-89">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="max-w-4xl mx-auto text-center" data-id="element-90">
          {/* Small decorative element */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="flex justify-center mb-8" data-id="element-91">
            <svg className="w-16 h-8 text-sage" viewBox="0 0 64 32" fill="none" aria-hidden="true" data-id="element-92">
              <path d="M32 4C32 4 20 12 12 20C8 24 4 28 4 28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" data-id="element-93" />
              <path d="M32 4C32 4 44 12 52 20C56 24 60 28 60 28" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" data-id="element-94" />
              <circle cx="32" cy="4" r="2" fill="currentColor" opacity="0.4" data-id="element-95" />
            </svg>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium text-botanical-text leading-tight mb-6" data-id="element-96">
            Nurture Your Body,
            <br data-id="element-97" />
            <span className="text-sage-dark italic" data-id="element-98">Calm Your Mind</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} className="font-sans text-lg md:text-xl text-botanical-muted max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-99">
            Experience the gentle art of therapeutic bodywork in a sanctuary
            designed to restore balance and cultivate inner peace. Let nature's
            wisdom guide your journey to wellness.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.6
        }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-100">
            <a href="#services" className="inline-flex items-center justify-center px-8 py-4 bg-olive text-cream font-sans font-medium rounded-full hover:bg-olive-dark transition-colors duration-300 shadow-botanical" data-id="element-101">
              Explore Services
            </a>
            <a href="#location" className="inline-flex items-center justify-center px-8 py-4 bg-transparent text-olive border-2 border-olive font-sans font-medium rounded-full hover:bg-olive hover:text-cream transition-colors duration-300" data-id="element-102">
              Book a Session
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 1.2
      }} className="absolute bottom-8 left-1/2 -translate-x-1/2" data-id="element-103">
          <motion.div animate={{
          y: [0, 8, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="flex flex-col items-center text-sage" data-id="element-104">
            <span className="text-sm font-sans mb-2 opacity-60" data-id="element-105">Scroll</span>
            <svg className="w-5 h-5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-106">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" data-id="element-107" />
            </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}